/**
 * Floor plans — the one image per unit type that goes in a brochure or a
 * listing page.
 *
 * Mirrors `housing/serializers.py`. Two models, and the split matters: a
 * `IFloorPlanRender` is an attempt, an `IFloorPlan` is a version somebody
 * accepted. Only the second is ever shown outside the wizard.
 */

export type FloorPlanRenderStatus = 'pending' | 'running' | 'succeeded' | 'failed';

/** One call to the image model. Revisions chain through `parent`. */
export interface IFloorPlanRender {
  id: number;
  design: number;
  unit: number | null;
  /** The `IDesignLevel` (storey) this attempt is for. */
  level: number;
  /** The render this one revises. Null on the first attempt of a chain. */
  parent: number | null;
  status: FloorPlanRenderStatus;
  /** The server's wording for `status` — show this, not the raw value. */
  status_label: string;
  /** What the user asked for. The house boilerplate is not included. */
  instruction: string;
  model: string;
  /** The model's output, untouched. */
  image_url: string | null;
  /** The same image with its background cut. What gets published. */
  keyed_image_url: string | null;
  key_params: {
    mode?: 'background' | 'all';
    white_point?: number;
    line_point?: number;
    width?: number;
    height?: number;
    transparent_fraction?: number;
  };
  /**
   * Share of the image made fully transparent. Near zero means the model did
   * not draw on white, and no slider position will fix it — the plan needs a
   * different generation or a manual upload.
   */
  transparent_fraction: number | null;
  /** USD for this one call. Every revision is another one. */
  cost: number | null;
  error: string;
  /**
   * Drive provenance. `source_path` and `source_name` are **Drive-authored
   * text: untrusted**. Render as text, never as markup or as an href.
   */
  source_file_id: string;
  source_path: string;
  source_name: string;
  source_md5: string;
  source_mime: string;
  created_by: number | null;
  created_at: string;
  modified_at: string;
}

/** A published version. Immutable. */
export interface IFloorPlan {
  id: number;
  design: number;
  unit: number | null;
  /** The `IDesignLevel` (storey) this version is for. Versions count per level. */
  level: number;
  version: number;
  /**
   * Whether this is the version the design (or unit) currently points at.
   * Read through that pointer on the server rather than stored, so it cannot
   * disagree with the database.
   */
  is_current: boolean;
  image_url: string | null;
  source_image_url: string | null;
  origin: 'generated' | 'uploaded';
  render: number | null;
  source_file_id: string;
  source_path: string;
  source_md5: string;
  published_by: number | null;
  published_by_name: string | null;
  notes: string;
  created_at: string;
}

/** What the source picker will accept. Anything else cannot be sent. */
export const FLOOR_PLAN_SOURCE_MIMES = [
  'application/pdf', 'image/png', 'image/jpeg', 'image/webp',
];

/**
 * What a hand-made plan may be uploaded as. Images only: an upload is published
 * as it is, and a PDF would need the redraw that uploading skips. PNG keeps a
 * transparent background; the others arrive opaque.
 */
export const FLOOR_PLAN_UPLOAD_MIMES = ['image/png', 'image/jpeg', 'image/webp'];

export type LevelLayout = '' | 'rows' | 'columns';

/**
 * One storey of a design: "Planta baja", "Planta alta". Every design has at
 * least level 0, and most have only that. Each level has its own floor plan,
 * and a brochure gets one floor-plan slide per level.
 *
 * `name` is localized: the API reads it in the request's language and writes
 * `name__es` / `name__en`.
 */
export interface IDesignLevel {
  id: number;
  design: number;
  /** Order, bottom to top. Not a floor number. */
  index: number;
  name: string | null;
  /** Moves only by publishing a plan for this level. */
  current_floor_plan: number | null;
  /** Rows or columns on the brochure slide; empty lets the drawing decide. */
  layout_override: LevelLayout;
}

/** A unit's own plan for one level of its design. Normally absent. */
export interface IUnitLevel {
  id: number;
  unit: number;
  level: number;
  current_floor_plan: number | null;
  layout_override: LevelLayout;
}

/** How a level reads in the office: its name, else its position. */
export const levelLabel = (level: Pick<IDesignLevel, 'index' | 'name'>) =>
  level.name || `Level ${level.index + 1}`;
